import { useQuery } from "@tanstack/react-query";
import Hey from "@/lib/hey-utils";
import { useState, useEffect } from "react";
import { parseISO, differenceInMinutes } from 'date-fns';

// Helper function to detect mobile devices
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Regular expression for mobile devices
  const mobileRegex = /android|avantgo|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
  
  // Regular expression for tablets (to include iPads)
  const tabletRegex = /android|ipad|playbook|silk/i;
  
  // Check if screen width is mobile-like (max-width: 768px)
  const isMobileWidth = window.innerWidth <= 768;
  
  return mobileRegex.test(userAgent) || 
         tabletRegex.test(userAgent) || 
         isMobileWidth;
};

interface CheckinUrl {
  href: string;
  channel: "WEB" | "MOBILE";
}

interface CheckinData {
  checkinWindowOpenDuration: string;
  checkinUrl: CheckinUrl[];
  airlineCode: string;
  languageCode: string;
  flightNumber: string;
  bookingReference?: string;
  lastName?: string;
}

export const useCheckinUrls = (tripReference: string) => {
  const [checkinUrls, setCheckinUrls] = useState<CheckinData[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  
  // Update mobile state on mount and window resize
  useEffect(() => {
    const updateMobileState = () => {
      const mobile = isMobileDevice();
      console.debug(`📱 Device detection: Mobile=${mobile}, Width=${window.innerWidth}px`);
      setIsMobile(mobile);
    };
    
    updateMobileState();
    window.addEventListener('resize', updateMobileState);
    return () => window.removeEventListener('resize', updateMobileState);
  }, []);
  
  console.debug(`🛫 useCheckinUrls hook called with trip reference: ${tripReference}`);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['checkin-urls', tripReference],
    queryFn: async () => {
      if (!tripReference) {
        console.debug('🛑 No trip reference provided to useCheckinUrls hook');
        return null;
      }
      
      console.debug(`🔍 Fetching checkin URLs for trip: ${tripReference}`);
      
      try {
        const result = await Hey.HeyUtils.getInstance().tripService.getAirlineCheckinUrls(tripReference);
        console.debug(`✅ Successfully retrieved checkin URLs for trip ${tripReference}:`, result);
        return result;
      } catch (error) {
        console.error(`❌ Error fetching checkin URLs for trip ${tripReference}:`, error);
        return null;
      }
    },
    enabled: !!tripReference,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  
  useEffect(() => {
    if (data) {
      console.debug(`📋 Setting checkin URLs from data for trip ${tripReference}:`, data);
      setCheckinUrls(data);
    } else {
      console.debug(`ℹ️ No checkin URL data available for trip ${tripReference}`);
    }
  }, [data, tripReference]);
  
  const getCheckinUrlForFlight = (flightNumber: string, departureDate?: string) => {
    console.debug(`🔎 Looking for checkin URL for flight ${flightNumber} in trip ${tripReference}`);
    console.debug('Current checkinUrls state:', checkinUrls);
    
    if (!checkinUrls || !checkinUrls.length) {
      console.debug(`⚠️ No checkin URLs available for flight ${flightNumber}`);
      return { url: null, windowOpen: false, hoursUntilOpen: 0 };
    }
    
    // Extract flight number without airline code (e.g., "EK565" -> "565")
    const flightNumberOnly = flightNumber.replace(/^[A-Z]+/, '');
    console.debug(`✈️ Looking for flight number ${flightNumberOnly}`);
    
    const checkinData = checkinUrls.find(url => {
      console.debug('Checking entry:', url);
      return url.flightNumber === flightNumberOnly;
    });
    
    if (!checkinData) {
      console.debug(`⚠️ No checkin data found for flight ${flightNumber}`);
      return { url: null, windowOpen: false, hoursUntilOpen: 0 };
    }
    
    if (!checkinData.checkinUrl || !checkinData.checkinUrl.length) {
      console.debug(`⚠️ Checkin data exists for flight ${flightNumber} but no URLs are available`, checkinData);
      return { url: null, windowOpen: false, hoursUntilOpen: 0 };
    }
    
    // Check if the check-in window is open based on departure date and checkinWindowOpenDuration
    let windowOpen = false;
    let hoursUntilOpen = 0;
    
    if (departureDate && checkinData.checkinWindowOpenDuration) {
      try {
        const departureTime = parseISO(departureDate || "1970");
        const now = new Date();
        const hoursDifference = differenceInMinutes(departureTime, now) / 60;
        const windowDuration = parseInt(checkinData.checkinWindowOpenDuration, 10);
        
        console.debug(`⏱️ Hours until departure: ${hoursDifference}, Check-in window: ${windowDuration} hours`);
        
        // Check-in window is open if departure is within the window duration
        windowOpen = hoursDifference <= windowDuration;
        
        // Calculate hours until check-in window opens
        if (!windowOpen) {
          hoursUntilOpen = hoursDifference - windowDuration;
        }
        
        console.debug(`⏱️ Hours until check-in window opens: ${hoursUntilOpen}`);
      } catch (error) {
        console.error('Error calculating check-in window:', error);
      }
    }
    
    // Get web or mobile URL based on device
    const webUrl = checkinData.checkinUrl.find(url => url.channel === "WEB");
    const mobileUrl = checkinData.checkinUrl.find(url => url.channel === "MOBILE");
    
    // Choose appropriate URL based on device
    const url = isMobile && mobileUrl ? mobileUrl.href : (webUrl ? webUrl.href : null);
    
    if (url) {
      console.debug(`✅ Found check-in URL for flight ${flightNumber}:`, url);
      console.debug(`🔓 Check-in window is ${windowOpen ? 'OPEN' : 'CLOSED'}`);
      return { url, windowOpen, hoursUntilOpen };
    } else {
      console.debug(`⚠️ No suitable check-in URL found for flight ${flightNumber}`);
      return { url: null, windowOpen, hoursUntilOpen };
    }
  };
  
  return {
    checkinUrls,
    isLoading,
    error,
    getCheckinUrlForFlight
  };
};
